import { useQuery } from '@tanstack/react-query';
import { ApiError } from '@api/client';
import { getLocation, locationsQueryKeys } from '@api/locations';
import type { LocationShowResponse } from '@api/locations';

interface UseLocationDetailOptions {
  enabled?: boolean;
  // Seed the cache from a payload already in hand (e.g. a list row) so the
  // edit page can render instantly while the fresh detail fetch settles.
  initialData?: LocationShowResponse;
}

// Single-location fetch for the edit page (GRAIN-1930). Named `useLocationDetail`
// (not `useLocation`) so it doesn't shadow TanStack Router's own `useLocation`
// hook in AppRouter.tsx. Mirrors useDiscount: keyed by `detail(id)`, short
// staleTime, and no retry on 4xx (a 404 won't resolve by retrying — the route
// redirects to the list instead).
export function useLocationDetail(
  id: number,
  { enabled = true, initialData }: UseLocationDetailOptions = {},
) {
  return useQuery({
    queryKey: locationsQueryKeys.detail(id),
    queryFn: () => getLocation(id),
    staleTime: 30_000,
    enabled,
    // Seed as fresh-at-mount so the edit page renders the passed-in detail
    // without an immediate refetch (mirrors useDiscount).
    ...(initialData ? { initialData, initialDataUpdatedAt: Date.now() } : {}),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 3;
    },
  });
}
